import { isNotNullable } from '@spendesk/general-type-helpers/misc';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import {
  type ApprovalWorkflowExport,
  type ApprovalWorkflowBranchExport,
  type ApprovalSchemeExport,
  type ApprovalWorkflowValuesExport,
} from './approvalWorkflowExportTypes';
import { collectDimensions, getDimensionNameKey } from './dimension';
import { ensureOtherValuesNodeLast } from './ensureOtherValuesNodeLast';
import { getDimensionTitle } from './getDimensionTitle';
import { type DimensionName } from '../../approvalWorkflow';
import {
  type Table,
  type TableCell,
  type TableRow,
  buildCell,
  buildEmptyRow,
  buildRow,
  buildTable,
} from '../spreadsheet/spreadsheetModel';

const getDimensionNodeHeader = (
  dimension: DimensionName,
  t: TGlobalFunctionTyped,
): string => {
  switch (dimension.type) {
    case 'costCenter':
      return t('approvalWorkflows.export.dimensions.costCenter.node');
    case 'spendType':
      return t('approvalWorkflows.export.dimensions.spendType.node');
    case 'team':
      return t('approvalWorkflows.export.dimensions.team.node');
    case 'expenseCategory':
      return t('approvalWorkflows.export.dimensions.expenseCategory.node');
    case 'customField':
      return t('approvalWorkflows.export.dimensions.customField.node', {
        customFieldName: dimension.name,
      });
    default:
      throw new TypeGuardError(dimension, 'Unknown dimension type');
  }
};

type DimensionNodeValues = {
  dimension: DimensionName;
  nodeIndex: number;
  values: ApprovalWorkflowValuesExport;
};

type DimensionNodeValuesByKey = Map<string, DimensionNodeValues>;

const buildValueCells = (
  values: ApprovalWorkflowValuesExport,
  t: TGlobalFunctionTyped,
): TableCell[] => {
  if (values === null) {
    return [
      buildCell({
        content: t('approvalWorkflows.export.otherValues'),
        bold: true,
      }),
    ];
  }
  return values.map((content) => buildCell({ content }));
};

export const exportApprovalWorkflowAsFlatTable = (
  workflow: ApprovalWorkflowExport,
  t: TGlobalFunctionTyped,
): Table => {
  const dimensions = collectDimensions(workflow.branch);

  const rows: TableRow[] = [];

  // Header row
  const headerRow = buildRow(
    [
      ...dimensions
        .values()
        .flatMap((dimension) => [
          getDimensionNodeHeader(dimension, t),
          getDimensionTitle(dimension, t),
        ]),
      t('approvalWorkflows.export.approvalSchemeId'),
    ].map((content) => buildCell({ content })),
  );
  rows.push(headerRow);

  const columnIndexByDimension = new Map(
    [...dimensions.keys()].map((dimensionKey, index) => [
      dimensionKey,
      { node: index * 2, type: index * 2 + 1 },
    ]),
  );
  const rowSize = headerRow.cells.length;
  const approvalSchemeColumnIndex = rowSize - 1;

  const expandNodeValues = (
    referenceRow: TableRow,
    nodeIndex: number,
    dimensionKey: string,
    values: ApprovalWorkflowValuesExport,
    scheme?: ApprovalSchemeExport,
  ): TableRow[] =>
    buildValueCells(values, t)
      .map((valueCell) => {
        const rowCells = [...referenceRow.cells];
        const columnIndex = columnIndexByDimension.get(dimensionKey);
        if (columnIndex === undefined) {
          return null;
        }
        rowCells[columnIndex.node] = buildCell({ content: `${nodeIndex}` });
        rowCells[columnIndex.type] = valueCell;
        rowCells[approvalSchemeColumnIndex] = buildCell({
          content: scheme?.id,
        });
        return buildRow(rowCells);
      })
      .filter(isNotNullable);

  const buildPathRows = (
    path: DimensionNodeValuesByKey,
    scheme?: ApprovalSchemeExport,
  ): TableRow[] =>
    [...path.entries()].reduce<TableRow[]>(
      (rowAccumulator, [dimensionKey, { nodeIndex, values }]) =>
        (rowAccumulator.length === 0
          ? [buildEmptyRow(rowSize)]
          : rowAccumulator
        ).flatMap((row) =>
          expandNodeValues(row, nodeIndex, dimensionKey, values, scheme),
        ),
      [],
    );

  // - at each level keep the approval scheme
  // - when reaching a leaf record a row
  const traverseBranch = (
    path: DimensionNodeValuesByKey,
    branch?: ApprovalWorkflowBranchExport,
    scheme?: ApprovalSchemeExport,
  ) => {
    if (!branch) {
      rows.push(...buildPathRows(path, scheme));
      return;
    }
    ensureOtherValuesNodeLast(branch.nodes).forEach((node, nodeIndex) => {
      const nodePath = new Map(path);
      nodePath.set(getDimensionNameKey(branch.dimension), {
        dimension: branch.dimension,
        nodeIndex,
        values: node.values,
      });
      traverseBranch(nodePath, node.branch, node.approvalScheme ?? scheme);
    });
  };

  traverseBranch(new Map(), workflow.branch, workflow.defaultApprovalScheme);

  return buildTable({ headerRowCount: 1, frozenRowCount: 1, rows });
};
